import {
  MovableCardsContainerCardViewModel,
  MovableCardsContainerGroupTypeEnum,
  MovableCardsContainerViewModel,
} from '../movable-cards-container.view-model';

type Layout<T extends string> = MovableCardsContainerViewModel<T>;
type Group<T extends string> = Layout<T>['groups'][number];
type Card<T extends string> = MovableCardsContainerCardViewModel<T>;

const collectKeys = <T extends string>(layout: Layout<T>): Set<T> => {
  const keys = new Set<T>();
  layout.groups.forEach((group) => {
    if (group.type === MovableCardsContainerGroupTypeEnum.SingleColumn) {
      group.cards.forEach((card) => keys.add(card.key));
    } else {
      group.leftCards.forEach((card) => keys.add(card.key));
      group.rightCards.forEach((card) => keys.add(card.key));
    }
  });
  return keys;
};

// Drop any card keys that no longer exist in the default layout.
const dropStaleKeys = <T extends string>(group: Group<T>, defaultKeys: Set<T>): Group<T> =>
  group.type === MovableCardsContainerGroupTypeEnum.SingleColumn
    ? { ...group, cards: group.cards.filter((card) => defaultKeys.has(card.key)) }
    : {
        ...group,
        leftCards: group.leftCards.filter((card) => defaultKeys.has(card.key)),
        rightCards: group.rightCards.filter((card) => defaultKeys.has(card.key)),
      };

// Insert any default cards not yet placed anywhere in the reconciled layout, at the index they
// occupy by default within this column. Cards already placed (elsewhere) are skipped, and every
// inserted card is marked placed so a later pass never inserts it again.
const insertMissingCards = <T extends string>(defaultCards: Card<T>[], targetCards: Card<T>[], placed: Set<T>): void => {
  defaultCards.forEach((card, index) => {
    if (!placed.has(card.key)) {
      targetCards.splice(index, 0, { key: card.key });
      placed.add(card.key);
    }
  });
};

// Fills a persisted group with any of the matching default group's cards that are not yet placed.
// Only applies when both groups are the same type — a persisted group can't gain a second column.
const mergeMissingCardsIntoGroup = <T extends string>(defaultGroup: Group<T>, targetGroup: Group<T>, placed: Set<T>): void => {
  if (
    defaultGroup.type === MovableCardsContainerGroupTypeEnum.SingleColumn &&
    targetGroup.type === MovableCardsContainerGroupTypeEnum.SingleColumn
  ) {
    insertMissingCards(defaultGroup.cards, targetGroup.cards, placed);
  } else if (
    defaultGroup.type === MovableCardsContainerGroupTypeEnum.TwoColumns &&
    targetGroup.type === MovableCardsContainerGroupTypeEnum.TwoColumns
  ) {
    insertMissingCards(defaultGroup.leftCards, targetGroup.leftCards, placed);
    insertMissingCards(defaultGroup.rightCards, targetGroup.rightCards, placed);
  }
};

// Builds a copy of a default group containing only the cards not yet placed anywhere in the
// reconciled layout. Used to recover cards whose default group has no type-matching counterpart in
// the persisted layout (e.g. the whole group was absent because none of its cards were visible when
// the layout was last saved).
const buildMissingCardsGroup = <T extends string>(defaultGroup: Group<T>, placed: Set<T>): Group<T> => {
  if (defaultGroup.type === MovableCardsContainerGroupTypeEnum.SingleColumn) {
    const cards = defaultGroup.cards.filter((card) => !placed.has(card.key));
    cards.forEach((card) => placed.add(card.key));
    return { ...defaultGroup, cards };
  }

  const leftCards = defaultGroup.leftCards.filter((card) => !placed.has(card.key));
  const rightCards = defaultGroup.rightCards.filter((card) => !placed.has(card.key));
  leftCards.forEach((card) => placed.add(card.key));
  rightCards.forEach((card) => placed.add(card.key));
  return { ...defaultGroup, leftCards, rightCards };
};

const isGroupEmpty = <T extends string>(group: Group<T>): boolean =>
  group.type === MovableCardsContainerGroupTypeEnum.SingleColumn
    ? group.cards.length === 0
    : group.leftCards.length === 0 && group.rightCards.length === 0;

// Fold any two-column group directly following another two-column group into it (left cards onto
// left cards, right cards onto right cards), so recovered groups appended by the pass above don't
// show up as a separate, oddly-narrow group next to an existing two-column layout.
const mergeAdjacentTwoColumnGroups = <T extends string>(groups: Group<T>[]): Group<T>[] =>
  groups.reduce<Group<T>[]>((merged, group) => {
    const previousGroup = merged.at(-1);
    if (
      previousGroup?.type === MovableCardsContainerGroupTypeEnum.TwoColumns &&
      group.type === MovableCardsContainerGroupTypeEnum.TwoColumns
    ) {
      merged[merged.length - 1] = {
        ...previousGroup,
        leftCards: [...previousGroup.leftCards, ...group.leftCards],
        rightCards: [...previousGroup.rightCards, ...group.rightCards],
      };
      return merged;
    }

    merged.push(group);
    return merged;
  }, []);

// A layout persisted before a new card was introduced will not contain that card's key, and the
// movable cards container only renders keys present in the layout. Merge any card keys that exist in
// the default layout but are missing from the persisted one back in (and drop stale keys no longer in
// the default), so newly added cards always appear:
//  1. For each default group with a same-type counterpart at the same index in the persisted layout,
//     insert its missing cards into that counterpart at their default column position.
//  2. Any default cards still missing after that (their default group had no type-matching persisted
//     counterpart — e.g. the whole group was absent) are appended as new group(s) at the end, rather
//     than guessing where they used to belong.
export const reconcileCardsLayout = <T extends string>(persisted: Layout<T> | undefined, defaultLayout: Layout<T>): Layout<T> => {
  if (!persisted) {
    return defaultLayout;
  }

  const persistedKeys = collectKeys(persisted);
  const defaultKeys = collectKeys(defaultLayout);

  const hasMissingDefaultKey = [...defaultKeys].some((key) => !persistedKeys.has(key));
  const hasStaleKey = [...persistedKeys].some((key) => !defaultKeys.has(key));

  // Nothing to reconcile: keep the user's saved arrangement untouched.
  if (!hasMissingDefaultKey && !hasStaleKey) {
    return persisted;
  }

  const reconciled: Layout<T> = { groups: persisted.groups.map((group) => dropStaleKeys(group, defaultKeys)) };
  const placed = collectKeys(reconciled);

  defaultLayout.groups.forEach((defaultGroup, groupIndex) => {
    const targetGroup = reconciled.groups[groupIndex];
    if (targetGroup && targetGroup.type === defaultGroup.type) {
      mergeMissingCardsIntoGroup(defaultGroup, targetGroup, placed);
    }
  });

  defaultLayout.groups.forEach((defaultGroup) => {
    const missingCardsGroup = buildMissingCardsGroup(defaultGroup, placed);
    if (!isGroupEmpty(missingCardsGroup)) {
      reconciled.groups.push(missingCardsGroup);
    }
  });

  return { groups: mergeAdjacentTwoColumnGroups(reconciled.groups) };
};
